import React, { useState, useCallback, useMemo } from "react";

import { useUpdateCompanyApplicationMutation, useGetCurrentCompanyApplicationQuery } from "EmployerApplication/api";
import { BusinessTenure, CompanyName, PhysicalAddress1, PhysicalCity, PhysicalState, PhysicalZip } from "EmployerApplication/Components/FinalFormQuestions";
import createDecorator from "final-form-focus";
import isIntuit from "Intuit/isIntuit";
import { Form } from "react-final-form";
import { OnChange } from "react-final-form-listeners";
import AnalyticsTracking from "Utils/AnalyticsTracking";

import { Body2 } from "Cobalt/BodyText";
import Button from "Cobalt/Button";
import { Callout } from "Cobalt/Callout";
import { Subtitle } from "Cobalt/Header";
import { ModalBody, ModalFooter } from "Cobalt/Modal";
import AppearingElement from "Common/AppearingElement";
import { Row, Col } from "Common/Grid";
import Icon from "Common/Icon";
import { SizeSwitch } from "Common/Responsive";

import makeBem from "Utils/makeBem";

const bem = makeBem("AddAncillaryPlanModal");
const focusOnErrors = createDecorator();

const BusinessTenurePage = React.memo(({ setCurrentPage, onHide }) => {
  const [updateCompanyApplication] = useUpdateCompanyApplicationMutation();
  const { data: companyApplication } = useGetCurrentCompanyApplicationQuery();
  const companyPolicyId = companyApplication?.id;

  const eligibleForDisabilityNull = companyApplication?.eligibleForDisability === null;
  const [ineligibleForDisability, setIneligibleForDisability] = useState(companyApplication?.eligibleForDisability === false);

  const initialValues = useMemo(
    () => ({
      company_application: {
        eligible_for_disability: eligibleForDisabilityNull ? undefined : companyApplication?.eligibleForDisability ? "Yes" : "No",
        company_name: companyApplication?.companyName,
        physical_address_one: companyApplication?.physicalAddressOne,
        physical_city: companyApplication?.physicalCity,
        physical_state: companyApplication?.physicalState,
        physical_zip: companyApplication?.physicalZip,
      },
    }),

    [companyApplication, eligibleForDisabilityNull],
  );

  const handleEligibilityChange = useCallback((value, previous) => {
    if (value !== previous) {
      AnalyticsTracking.trackEvent("cta_clicked", {
        label: "has_company_been_in_business_for_two_years",
        value,
      });

      if (value === "No") {
        AnalyticsTracking.trackEvent("cta_clicked", {
          label: "disability_company_age_denied",
          value,
        });
      }
      setIneligibleForDisability(value === "No");
    }
  }, []);

  const handleSubmit = async (values) => {
    await updateCompanyApplication({
      id: companyPolicyId,
      ...values.company_application,
    });

    onHideOrSetCurrentPage(values.company_application?.eligible_for_disability);
  };

  const onHideOrSetCurrentPage = useCallback(
    (eligible_for_disability) => {
      if (eligible_for_disability === "No") {
        onHide();
      } else {
        setCurrentPage("sicPage");
      }
    },
    [onHide, setCurrentPage],
  );

  const hasMissingValues = () =>
    !companyApplication?.companyName ||
    !companyApplication?.physicalAddressOne ||
    !companyApplication?.physicalCity ||
    !companyApplication?.physicalState ||
    !companyApplication?.physicalZip;

  if (!companyPolicyId) return;

  return (
    <Form onSubmit={handleSubmit} decorators={[focusOnErrors]} initialValues={initialValues}>
      {({ handleSubmit, values }) => (
        <form onSubmit={handleSubmit}>
          <ModalBody>
            <Row mx={1}>
              {hasMissingValues() && (
                <>
                  <Subtitle pb={isIntuit() ? 2 : 4}>Company Information</Subtitle>
                  <CompanyName pl={0} />
                  <PhysicalAddress1 pl={0} />
                  <PhysicalCity pl={0} />
                  <PhysicalState pl={0} />
                  <PhysicalZip pl={0} />
                </>
              )}

              <BusinessTenure pl={0} />
              <OnChange name="company_application[eligible_for_disability]">{handleEligibilityChange}</OnChange>
              <AppearingElement show={values.company_application?.eligible_for_disability === "No"}>
                <IneligibilityMessage />
              </AppearingElement>
            </Row>
          </ModalBody>
          <Footer
            ineligibleForDisability={ineligibleForDisability}
            onNext={() => handleSubmit(values)}
            onBack={() => {
              if (hasMissingValues()) {
                handleSubmit(values); // even if not eligible, we want to capture the company info
              }
              setCurrentPage(null);
              onHide();
            }}
          />
        </form>
      )}
    </Form>
  );
});

const IneligibilityMessage = () => (
  <Callout
    type="warning"
    className="my-2 w-8/12 md:w-10/12 max-w-full"
    body={
      <Body2 py={2} ml={1}>
        Unfortunately, we are not able to offer disability insurance for your business at this time.
      </Body2>
    }
  />
);

const BackButton = ({ onBack }) =>
  onBack && (
    <Button color="tertiary" className="pull-left" onClick={onBack}>
      <Icon icon="chevron-left" pr={3} />
      Back
    </Button>
  );

const NextButton = ({ onNext, ineligibleForDisability, fullWidth }) =>
  ineligibleForDisability ? (
    <Button color="primary" type="submit" className="float-right" fullWidth={fullWidth}>
      Finish
    </Button>
  ) : (
    <Button color="primary" onClick={onNext} className="float-right" fullWidth={fullWidth}>
      Continue
    </Button>
  );

const Footer = ({ ineligibleForDisability, onNext, onBack }) => (
  <ModalFooter className="text-left">
    <SizeSwitch
      sm={
        <Row className={bem("numberedFooter")}>
          <BackButton onBack={onBack} />
          <NextButton ineligibleForDisability={ineligibleForDisability} onNext={onNext} fullWidth={false} />
        </Row>
      }
      xs={
        <div className={bem("mobileFooter")}>
          <BackButton onBack={onBack} />
          <NextButton ineligibleForDisability={ineligibleForDisability} onNext={onNext} />
        </div>
      }
    />
  </ModalFooter>
);

export default BusinessTenurePage;
